import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isNumberGreaterThanZero } from '@thesavingsgroup/utils/isNumberGreaterThanZero';
import i18n from 'i18next';
import { isEmpty, toNumber } from 'lodash';
import { StateCreator } from 'zustand';

import { isInEnum } from '../../utils/isInEnum';
import { isValidVin } from '../applyStore/functions/isValidVin';
import { PreviewState } from './types/PreviewState';
import { PreviewStateDependencies } from './types/PreviewStateDependencies';
import { SetPreviewActivationStateParams } from './types/SetPreviewActivationStateParams';

export const keysToPersist = [
  'firstName',
  'lastName',
  'primaryEmailAddress',
  'primaryPhoneNumber',
];

export const previewStore =
  ({
    useLeadChannelStore,
    useParameterStore,
  }: PreviewStateDependencies): StateCreator<PreviewState> =>
  (set) => ({
    firstName: '',
    lastName: '',
    primaryEmailAddress: '',
    primaryPhoneNumber: '',

    computed: {
      get applyRoute(): string {
        const { applicationType, vehicle_vin } = useParameterStore.getState();

        if (applicationType === ApplicationType.PURCHASE) {
          // we do not support collection of purchase type via parameters, so we route
          // purchase preview to the purchase type selection page.
          return '/apply/purchase-type';
        }

        if (isValidVin(vehicle_vin)) {
          // If there's a valid VIN, we can skip to confirming vehicle details.
          return '/apply/confirm-vehicle';
        } else if (applicationType === ApplicationType.LEASE_BUYOUT) {
          // when there's an invalid VIN for a lease buyout preview, we route to the search vehicle page.
          return '/apply/search-vehicle';
        }

        // In any other case route to the vehicle selection page.
        return '/apply/vehicle-selection';
      },
      get hasValidPreviewOffer(): boolean {
        const { amount, apr, payment, term } = useParameterStore.getState();

        return [
          toNumber(amount),
          toNumber(apr),
          toNumber(payment),
          toNumber(term),
        ].every(isNumberGreaterThanZero);
      },
      get titleText(): string {
        const { applicationType } = useParameterStore.getState();
        const {
          computed: { brand, displayLabel },
        } = useLeadChannelStore.getState();

        const isDefaultTitle =
          !isInEnum(applicationType, ApplicationType) ||
          isEmpty(brand) ||
          isEmpty(displayLabel);

        return isDefaultTitle
          ? i18n.t('PreviewOfferPage.title.default')
          : i18n.t(`PreviewOfferPage.title.${applicationType}`, {
              brand,
              displayLabel,
            });
      },
    },

    setPreviewActivationState: (
      params: SetPreviewActivationStateParams,
    ): void => {
      const {
        firstNameValue,
        lastNameValue,
        primaryEmailAddressValue,
        primaryPhoneNumberValue,
      } = params;

      set({
        firstName: firstNameValue,
        lastName: lastNameValue,
        primaryEmailAddress: primaryEmailAddressValue,
        primaryPhoneNumber: primaryPhoneNumberValue,
      });
    },
  });
